/**
 * EventCard.tsx
 *
 * Grid card for "Your Events" listings.
 * Shows cover image, status badge, genres, title, description, date, location.
 */

import React from 'react';
import { FiCalendar, FiMapPin, FiClock } from 'react-icons/fi';
import { type MockEvent } from '../../utils/eventMockData';

interface EventCardProps {
  event: MockEvent;
  onClick?: () => void;
}

function formatEventDate(start?: string): string {
  if (!start) return '';
  const s = new Date(start);
  const opts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  };
  return new Intl.DateTimeFormat('en-US', opts).format(s);
}

function formatEventTime(start?: string): string {
  if (!start) return '';
  const s = new Date(start);
  return s.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' });
}

function formatTimeRange(start?: string, end?: string): string {
  if (!start) return '';
  const s = formatEventTime(start);
  const e = end ? formatEventTime(end) : '';
  return e ? `${s} — ${e}` : s;
}

export const EventCard: React.FC<EventCardProps> = ({ event, onClick }) => {
  const cover = event.medias?.[0]?.file_url ?? '';

  return (
    <button
      onClick={onClick}
      className="group w-full text-left bg-zinc-900 rounded-2xl overflow-hidden border border-white/5 hover:border-white/15 transition-all duration-200 hover:scale-[1.02] cursor-pointer flex flex-col"
    >
      {/* Cover image */}
      <div className="relative w-full aspect-square bg-zinc-800 overflow-hidden">
        {cover ? (
          <img
            src={cover}
            alt={event.event_title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <FiCalendar size={32} className="text-zinc-700" />
          </div>
        )}
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-linear-to-b from-black/40 to-transparent" />

        {/* Status badge */}
        <div
          className={`absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold ${
            event.is_draft_or_published === 'published'
              ? 'bg-green-500/20 text-green-400 border border-green-500/30'
              : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
          }`}
        >
          <span
            className={`w-1.5 h-1.5 rounded-full ${event.is_draft_or_published === 'published' ? 'bg-green-400' : 'bg-blue-400'}`}
          />
          {event.is_draft_or_published === 'published' ? 'Published' : 'Draft'}
        </div>
      </div>

      {/* Info */}
      <div className="p-4 space-y-2.5 flex-1 flex flex-col">
        {/* Genre tags */}
        {event.genres && event.genres.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {event.genres.slice(0, 2).map((g) => (
              <span
                key={g}
                className="px-2 py-0.5 rounded-full bg-zinc-800 border border-white/8 text-zinc-400 text-[11px] font-medium"
              >
                {g}
              </span>
            ))}
          </div>
        )}

        {/* Title */}
        <h3 className="text-white text-[14px] font-bold leading-tight line-clamp-1">
          {event.event_title}
        </h3>

        {/* Description */}
        <p className="text-zinc-500 text-[12px] leading-relaxed line-clamp-2 flex-1">
          {event.info}
        </p>

        {/* Date & Time */}
        <div className="flex items-center gap-1.5 text-zinc-400 text-[11px]">
          <FiCalendar size={11} className="shrink-0 text-blue-400" />
          <span>{formatEventDate(event.start)}</span>
          <span className="text-zinc-600 mx-1">·</span>
          <FiClock size={11} className="shrink-0 text-blue-400" />
          <span>{formatTimeRange(event.start, event.end)}</span>
        </div>

        {/* Location */}
        <div className="flex items-center gap-1.5 text-zinc-400 text-[11px]">
          <FiMapPin size={11} className="shrink-0 text-blue-400" />
          <span className="font-medium text-zinc-300">{event.venue}</span>
          <span className="text-zinc-600">·</span>
          <span className="truncate">{[event.city, event.country].filter(Boolean).join(', ')}</span>
        </div>
      </div>
    </button>
  );
};
